import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const AudioPlayer = () => {
  const { t } = useLang();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const audio = new Audio("/audio/violon-printemps.mp3");
    audio.loop = true;
    audio.volume = 0.4;
    audioRef.current = audio;
    return () => {
      audio.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      try {
        await audio.play();
        setPlaying(true);
      } catch {
        setPlaying(false);
      }
    }
  };

  return (
    <motion.div
      data-testid="floating-audio-player"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 2.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed bottom-5 right-5 z-40 flex items-center gap-3 rounded-full border border-gold/30 bg-ink2/75 py-2 pl-2.5 pr-3 shadow-2xl shadow-black/50 backdrop-blur-xl sm:py-2.5 sm:pl-3 sm:pr-5 ${playing ? "" : "eq-paused"}`}
    >
      <button
        data-testid="audio-toggle-button"
        aria-label={playing ? t.audio.pause : t.audio.play}
        title={playing ? t.audio.pause : t.audio.play}
        onClick={toggle}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-gold text-ink transition-transform duration-300 hover:scale-105"
      >
        {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>
      <div className="hidden h-5 items-end gap-[3px] sm:flex" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className="eq-bar w-[3px] rounded-full bg-gold"
            style={{ height: "100%", animationDelay: `${i * 0.18}s`, animationDuration: `${0.8 + i * 0.12}s` }}
          />
        ))}
      </div>
      <div className="hidden leading-tight sm:block">
        <p data-testid="audio-track-title" className="font-mono text-[10px] uppercase tracking-[0.2em] text-parchment">
          {t.audio.title}
        </p>
        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-sand/70">{t.audio.artist}</p>
      </div>
    </motion.div>
  );
};

export default AudioPlayer;
